import React from "react";
import { DeploymentInfo } from "./Index";
import {
  getElapsedTime,
  isElapsedTimeLessThanThreshold,
} from "../../utils/time-service";
import { colors } from "../../colors";
import CenteredSpinner from "../../common/CenteredSpinner";
import deploymentIcon from "../../assets/deploy.png";
import infoIcon from "../../assets/info-circle.svg";
import { sendDeploymentToJenkins } from "./jenkinsService";
import { openInNewTab } from "../../utils/url-actions";

interface Environment {
  name: string;
  label: string;
}

interface FlattenedRepo {
  repoName: string;
  groupName: string;
}

interface TableProps {
  data: Record<string, Record<string, DeploymentInfo | null>>;
  envs: Environment[];
  repos: [string, FlattenedRepo[]][];
  Loading: boolean;
  handleClick: (
    repoName: string,
    envName: string,
    deploymentInfo: DeploymentInfo | null
  ) => void;
  searchQuery: string;
  selectedFilter: string;
}

const Table: React.FC<TableProps> = ({
  data,
  envs,
  repos,
  Loading,
  handleClick,
}) => {
  const handleDeployment = async (
    repoName: string,
    envName: string,
    branchName: string
  ) => {
    const data = await sendDeploymentToJenkins(repoName, envName, branchName);
    if (data.url) {
      openInNewTab(data.url);
    } else {
      alert("No action found corresponding to the request");
    }
  };
  return (
    <table className="min-w-full divide-y divide-gray-200">
      <thead
        style={{ backgroundColor: colors.tableHeader }}
        className="sticky top-0 z-1"
      >
        <tr>
          <th
            className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
            style={{ color: colors.tableHeaderText }}
          >
            Repo Name
          </th>
          {envs.map((env) => (
            <th
              key={env.name}
              className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
              style={{ color: colors.tableHeaderText }}
            >
              {env.label}
            </th>
          ))}
        </tr>
      </thead>
      {Loading ? (
        <tbody>
          <tr>
            <td colSpan={envs.length + 1}>
              <CenteredSpinner variant="fullheight" />
            </td>
          </tr>
        </tbody>
      ) : (
        <tbody className="bg-white divide-y divide-gray-200">
          {repos.map(([groupName, groupRepos], groupIndex) =>
            groupRepos.map((repo) => {
              return (
                <tr
                  key={repo.repoName}
                  style={{
                    backgroundColor:
                      groupIndex % 2 === 0
                        ? colors.evenRowBackground
                        : colors.oddRowBackground,
                  }}
                >
                  <td
                    className="px-6 py-4 whitespace-nowrap text-sm font-medium"
                    style={{ color: colors.tableDivisionColor }}
                  >
                    {repo.repoName}
                  </td>
                  {envs.map((env) => {
                    const deploymentInfo = data[repo.repoName]?.[env.name];
                    const cellClasses = `px-6 pt-4 pb-2 whitespace-nowrap text-sm text-gray-500 border border-gray-200 hover:bg-gray-100 cursor-pointer ${
                      deploymentInfo &&
                      isElapsedTimeLessThanThreshold(deploymentInfo.timestamp)
                        ? "bg-green-100"
                        : ""
                    } ${
                      deploymentInfo && deploymentInfo.inProgress
                        ? "bg-yellow-100 animate-pulse"
                        : ""
                    } ${
                      deploymentInfo && deploymentInfo.status === "failed"
                        ? ""
                        : ""
                    } ${
                      deploymentInfo && deploymentInfo.status === "sleeping"
                        ? "bg-slate-300 animate-pulse"
                        : ""
                    }`;

                    return (
                      <td key={env.name} className={cellClasses}>
                        {deploymentInfo ? (
                          <div className="flex flex-col gap-2">
                            {deploymentInfo.status === "failed" && (
                              <div>
                                <strong className="text-red-600 font-bold">
                                  Last Deployment Failed
                                </strong>
                              </div>
                            )}
                            {deploymentInfo.status === "sleeping" && (
                              <div>
                                <strong className="text-slate-500 font-bold">
                                  Pod is sleeping...
                                </strong>
                              </div>
                            )}
                            {deploymentInfo.inProgress && (
                              <div className="text-yellow-500">
                                <strong>In Progress...</strong>
                              </div>
                            )}
                            <div>
                              <strong>Timestamp:</strong>{" "}
                              {getElapsedTime(deploymentInfo.timestamp)}
                            </div>
                            <div>
                              <strong>Branch:</strong> {deploymentInfo.branch}
                            </div>
                            <div>
                              <strong>Deployed By:</strong>{" "}
                              {deploymentInfo.deployed_by}
                            </div>
                            <div>
                              <strong>DB Triggered:</strong>{" "}
                              {deploymentInfo.db_triggered ? "Yes" : "No"}
                            </div>
                            <div className="flex gap-2 border-t pt-1 mt-2">
                              <button
                                onClick={() =>
                                  handleClick(
                                    repo.repoName,
                                    env.name,
                                    deploymentInfo
                                  )
                                }
                                className="p-2 rounded hover:bg-slate-300"
                                aria-label="Info"
                                title="Info"
                              >
                                <img src={infoIcon} alt="Info" width={15} />
                              </button>
                              {env.name.toLocaleLowerCase() !== "prod" && (
                                <div>
                                  <button
                                    onClick={() => {
                                      handleDeployment(
                                        repo.repoName,
                                        env.name,
                                        deploymentInfo.branch
                                      );
                                    }}
                                    className="p-2 rounded hover:bg-slate-300"
                                    aria-label="Go to Deployment Pipeline"
                                    title="Go to Deployment Pipeline"
                                  >
                                    <img
                                      src={deploymentIcon}
                                      alt="Deployment Pipeline"
                                      width={15}
                                      height={15}
                                    />
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <span>No Data</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })
          )}
        </tbody>
      )}
    </table>
  );
};

export default Table;
